import {
    fetchCategoryTree,
    type CategoryTreeItem,
} from '~/composables/shopware/useCategorySiblings';
import { useShopwareContext } from '@shopware/composables';

interface UseCategorySiblingsDisplayOptions {
    category?: { id: string; parentId?: string };
    isSearchPage: Ref<boolean> | ComputedRef<boolean>;
    getCurrentListing: Ref<any> | ComputedRef<any>;
}

export function useCategorySiblingsDisplay(options: UseCategorySiblingsDisplayOptions) {
    const {
        category,
        isSearchPage,
        getCurrentListing,
    } = options;

    // Capture apiClient during setup (guaranteed component context) so the
    // useAsyncData handler doesn't need to call useShopwareContext() / inject()
    // inside an async callback where the component instance may not be active.
    const { apiClient } = useShopwareContext();

    const siblingsResult = useAsyncData(
        `category-siblings-nav-${category?.id ?? 'none'}`,
        () => {
            if (category?.parentId) {
                return fetchCategoryTree(category, apiClient);
            }
            return Promise.resolve([]);
        },
        {
            getCachedData(key, nuxtApp) {
                return nuxtApp.payload.data[key];
            },
        },
    );

    const _stableSiblings = ref<CategoryTreeItem[]>([]);
    watch(() => (siblingsResult.data.value || []).filter((s: CategoryTreeItem) => s.active), (filtered) => {
        if (filtered.length > 0) {
            _stableSiblings.value = filtered;
        }
    }, { immediate: true });

    const displayCategorySiblings = computed<CategoryTreeItem[]>(() => _stableSiblings.value);

    const siblingsLoading = computed(
        () => siblingsResult.status.value === 'pending' && _stableSiblings.value.length === 0,
    );

    const searchCategoryTree = computed(() => {
        if (!isSearchPage.value) return null;
        return (getCurrentListing.value as any)?.extensions?.categoryTree ?? null;
    });

    const { getUrlById: getCategorySeoUrl } = useCategorySeoUrl();

    const childCategories = computed(() => {
        if (!category?.id) return [];

        let currentCat = displayCategorySiblings.value.find(s => s.id === category?.id);
        if (!currentCat) {
            for (const sibling of displayCategorySiblings.value) {
                if (sibling.children) {
                    const found = sibling.children.find(c => c.id === category?.id);
                    if (found) {
                        currentCat = found;
                        break;
                    }
                }
            }
        }

        const children = currentCat?.children as CategoryTreeItem[] | undefined;
        if (!children?.length) return [];

        return children
            .filter(c => c.active)
            .map(c => ({
                ...c,
                resolvedUrl: c.seoUrls?.[0]?.seoPathInfo || getCategorySeoUrl(c.id)?.replace(/^\//, '') || null,
            }))
            .filter(c => c.resolvedUrl);
    });

    return {
        siblingsResult,
        displayCategorySiblings,
        siblingsLoading,
        searchCategoryTree,
        childCategories,
    };
}
